/**
 * 필터 필드 id — 그대로 폼 값 키가 된다. 1.0 MOMS_PROG_INFO(GR05) FILTER_CD 와 대응한다.
 *
 * 화주 두 칸(`logisticsServiceTypes`/`shipperNames`)은 이름을 바꾸지 말 것 — pbo-core
 * `logistics.ShipperSelect` 가 `typeName`/`nameName` 미지정 시 이 두 이름으로 값을 읽고 쓴다.
 * 둘 다 **배열**이다.
 */
export const FIELD_NAMES = {
  /** 1.0 LOGISTICS_SERVICE_TYPES — 클라이언트가 화주명 앞에 끼워 넣는 콤보(1.0 insertLogisticsServiceTypesFilter) */
  logisticsServiceTypes: 'logisticsServiceTypes',
  /** 1.0 SPR_NM (FILTER_TYP=SEARCH, 서치헬퍼) */
  shipperNames: 'shipperNames',
  /** 1.0 ORD_OPT_NO (TEXT_MULTI) — 입고번호 */
  ordOptionNumbers: 'ordOptionNumbers',
  /** 1.0 PLN_DATE (DATE_MULTI, BETWEEN) — [from, to] */
  plnDateRange: 'plnDateRange',
  /** 1.0 ACT_DATE (DATE_SINGLE, EQUAL) */
  actDate: 'actDate',
  /** 1.0 ORD_TYPE (COMBO) */
  orderType: 'orderType',
  /** 1.0 ORD_STATUS (COMBO) */
  orderStatus: 'orderStatus',
} as const

/**
 * 화주유형 옵션 — 1.0 `utils/shipperType.js` SPR_TYPE_FILTER 와 동일 목록.
 *
 * '전체' 는 여기에 넣지 않는다 — 화주유형/화주명 멀티셀렉트(산출물 `MultiSelectBox`)가 `allOption` 으로
 * 첫 항목에 자동 추가하고 기본 선택까지 잡는다. 여기 배열에 손으로 넣으면 중복이 된다.
 */
export const LOGISTICS_SERVICE_TYPE_OPTIONS = [
  { value: 'M1P', label: 'M1P' },
  { value: 'MFS', label: 'MFS' },
  { value: 'MWP', label: 'MWP' },
  { value: 'MLT', label: 'MLT' },
  { value: 'MCD', label: 'MCD' },
  { value: 'MTP', label: 'MTP' },
] as const

/**
 * 화주유형 기본 선택값 — MUSINSA(통합 운영 계정)의 첫 진입·초기화 기준값이다(개발 결정, main 동일).
 *
 * BE 는 화주유형과 화주명을 AND 로 건다. 화주 고정 계정은 이 값이 아니라 **세션 화주의 실제 유형**을
 * 쓴다 — M1P 로 못박으면 MLT·MWP 화주가 자기 데이터가 있어도 0건이 된다(index.tsx `ownServiceType`).
 */
export const DEFAULT_LOGISTICS_SERVICE_TYPE = 'M1P'

/** 화주유형 멀티셀렉트의 '전체' 항목 값 — 산출물 `MultiSelectBox allOption` 계약(빈 문자열). 요청 시 6종으로 펼친다. */
export const ALL_SERVICE_TYPES_VALUE = ''

/**
 * 입고유형 옵션 — moms-be `OrderType.displayName` 실측 11종이 정본이다.
 *
 * 핸드오프 데모(`inqAllOrdType.options`)에는 '재고이동'·'증정'·'폐기'·'테스트' 가 더 들어 있는데,
 * 2.0 은 이 enum 에 없는 값을 400 으로 막는다(AllInboundSearchService.buildSearchContext).
 * 시각 계약은 데모, **데이터 계약은 BE 실측**이 정본이라 옵션은 여기 목록을 쓴다(GR01 준용).
 * TODO(spec): 1.0 콤보에만 있는 값들을 어느 쪽에 맞출지 BE(심현아)·기획 확인 필요.
 */
export const ORDER_TYPE_OPTIONS = [
  { value: '일반', label: '일반' },
  { value: 'PLUS', label: 'PLUS' },
  { value: '사내', label: '사내' },
  { value: '사내(해외)', label: '사내(해외)' },
  { value: '명품', label: '명품' },
  { value: '반품', label: '반품' },
  { value: '해외', label: '해외' },
  { value: '해외(현지)', label: '해외(현지)' },
  { value: '교환', label: '교환' },
  { value: '기타', label: '기타' },
  { value: '미지정', label: '미지정' },
] as const

/**
 * 입고상태 옵션 — moms-be `InboundReportStatus.code` 실측 5종이 정본이다.
 *
 * 1.0 MOMS_COMBO_INFO(ORD_STATUS)와 핸드오프 데모(`inqAllOrdStatus.options`)에는 출고용 값
 * (미출고/출고완료/가송장)이 섞여 있다 — 이 화면 조건으로 보내면 무결과다. enum 을 정본으로 삼는다.
 */
export const ORDER_STATUS_OPTIONS = [
  { value: '입고대기', label: '입고대기' },
  { value: '부분입고', label: '부분입고' },
  { value: '입고완료', label: '입고완료' },
  { value: '과입고', label: '과입고' },
  { value: '입고취소', label: '입고취소' },
] as const

/**
 * 엑셀 다운로드 행수 상한 — BE `MAX_EXCEL_ROWS`(LOGISPRD-9555)와 동일.
 * ST02/ST07 계열의 100,000 이 아니라 **30,000** 이다. 서버도 초과 시 400 을 주므로 여기 선차단은 왕복 절약용.
 */
export const EXCEL_ROW_LIMIT = 30_000

export const MESSAGES = {
  /** 조회 결과 0건 — [조회] 버튼으로 실행했을 때만 띄운다(페이지 이동·조건 복원 진입은 제외). */
  searchNoRows: '조회된 데이터가 없습니다.',
  /**
   * 화주 필수 안내 — 필드가 '화주유형/화주명' 한 칸(멀티셀렉트 두 개)으로 합쳐졌으므로
   * 문구도 한 덩어리로 말한다(사용자 확정 2026-09-09). 종전 '화주명 또는 화주유형 중 하나는…' 은
   * 두 필드가 따로였던 시절의 표현이라 화면과 어긋났다.
   */
  requiredFilter: '화주유형/화주명을 선택해 주세요.',
  excelNoRows: '다운로드할 데이터가 없습니다.',
  excelRowLimitExceeded: `엑셀 다운로드 최대 행수(${EXCEL_ROW_LIMIT.toLocaleString()}행)를 초과했습니다. 조회 조건을 추가해 주세요.`,
} as const

/** 표 id(생성본 columnLayout 키) → gridCd — 화면관리 그리드 설정과 개인 컬럼 배치 저장이 같은 값을 쓴다(docs/conventions/grid-code.md) */
export const GRID_CDS = { inqAllTable: 'GR08_HEAD' } as const
